<script setup lang="ts">
import type { IOrgSignupFormModel } from "@chatfood/design-system";
import {
  OrgSignupForm,
  OrgValidateOtp,
  useToast,
  AtomLink,
  AtomText,
} from "@chatfood/design-system";
import { ref, computed } from "vue";
import {
  bemBuilder,
  formValidation,
  phoneBeautifier,
} from "@chatfood/core-utils";
import { isAuthRepoError } from "@chatfood/dbe-client/errors";
import { OtpDeliverySelector } from "../otp-delivery-selector";
import type { ISignUpCustomerProps } from "./props";
import type { ISignUpCustomerEmits } from "./emits";
import type { IDomainNewUser } from "@/domain/new-user";
import { useBusinessStore } from "~/stores/business";
import { useCustomerStore } from "~/stores/customer";
import { useLocale } from "~/composables/use-locale";
import { I18nTranslate } from "~/components/i18n-translate";
import { useBugReporter, useDirectBackend } from "#imports";
import {
  DELIVERECT_PRIVACY_POLICY,
  DELIVERECT_TERMS_AND_CONDITION,
} from "~/domain/constants";

const props = defineProps<ISignUpCustomerProps>();
const emits = defineEmits<ISignUpCustomerEmits>();

const { t } = useLocale();
const { report } = useBugReporter();
const { toastError, toastUnexpectedError } = useToast();

const css = bemBuilder("sign-up-customer");

const business = useBusinessStore();
const customer = useCustomerStore();

const newCustomer = ref<IDomainNewUser>({
  name: "",
  phoneNumber: {
    isValid: false,
  },
  acceptsMarketingCommunications: true,
  acceptsTermsConditions: false,
});

const displayPhoneNumber = computed<string>(() => {
  const countryCode =
    newCustomer.value.phoneNumber.countryCode?.toString() ?? "";
  const phoneNumber = newCustomer.value.phoneNumber.number?.toString() ?? "";

  if (!countryCode) {
    return phoneNumber;
  }

  const fullPhoneNumber = countryCode + phoneNumber;

  return phoneBeautifier(phoneNumber, countryCode) ?? fullPhoneNumber;
});

const view = ref<"form" | "otp-request" | "otp-validation">("form");

const isLoadingVerificationCode = ref(false);
const otpErrorMessage = ref<string>();

const formErrors = ref({});

const isFormValid = (request: IDomainNewUser) => {
  formErrors.value = {};

  const validationErrors = formValidation(
    {
      name: {
        required: {
          message: t("components.sign_up_customer.error_required_name"),
        },
        minLength: {
          limit: 3,
          message: t("components.sign_up_customer.error_name_min_chars", {
            min: 3,
          }),
        },
      },
    },
    {
      name: request.name,
    },
  );

  if (!request.phoneNumber.isValid) {
    validationErrors.phoneNumber = t(
      "components.sign_up_customer.error_invalid_phone_number",
    );
  }

  if (!request.acceptsTermsConditions) {
    validationErrors.acceptsTermsConditions = t(
      "components.sign_up_customer.error_terms_must_be_accepted",
    );
  }

  if (Object.keys(validationErrors).length > 0) {
    formErrors.value = validationErrors;

    return false;
  }

  return true;
};

const sendOtpViaEmail = async () => {
  if (!props.skipOtpSelector) return;

  try {
    await authRepo.requestOtp({
      businessId: business.id,
      messagingChannel: "email",
      key: "email",
      email: props.email,
    });
  } catch (err: unknown) {
    report(err);
    toastError(t("components.sign_in_customer.error_unknown"));
    emits("failure");
  }
};

const signupFormErrors = ref({});

const { authRepo } = useDirectBackend();

const onValidateOtp = async (otp: string) => {
  otpErrorMessage.value = undefined;
  signupFormErrors.value = {};
  isLoadingVerificationCode.value = true;

  try {
    await authRepo.signUp({
      businessId: business.id,
      email: props.email,
      verificationCode: otp,
      name: newCustomer.value.name,
      phoneNumber: newCustomer.value.phoneNumber.number?.toString() ?? "",
      countryCode: newCustomer.value.phoneNumber.countryCode
        ? `+${newCustomer.value.phoneNumber.countryCode}`
        : "",
      acceptsMarketingCommunications:
        newCustomer.value.acceptsMarketingCommunications,
    });

    await onAuthSuccess();
  } catch (err: unknown) {
    isLoadingVerificationCode.value = false;

    if (isAuthRepoError(err)) {
      if (err.isValidation() && err.hasMeta()) {
        signupFormErrors.value = err.meta;
        newCustomer.value = {
          ...newCustomer.value,
          phoneNumber: {
            isValid: false,
          },
        };

        view.value = "form";
        toastError(t("components.sign_up_customer.error_generic_form_toast"));
        return;
      }

      if (err.isTooManyRequests()) {
        toastError(
          t("components.sign_up_customer.error_too_many_otp_validation"),
        );

        emits("failure");
        return;
      }

      otpErrorMessage.value = t(
        "components.sign_up_customer.error_invalid_otp",
      );

      return;
    }

    otpErrorMessage.value =
      err instanceof Error
        ? err.message
        : t("components.sign_up_customer.error_unknown");

    report(err);
  }
};

const onSignupSubmit = async (payload: IOrgSignupFormModel) => {
  const isValid = isFormValid(payload);

  if (!isValid) {
    signupFormErrors.value = formErrors.value;
    return;
  }

  signupFormErrors.value = {};

  if (props.skipOtpSelector) {
    view.value = "otp-validation";
    await sendOtpViaEmail();
  } else {
    view.value = "otp-request";
  }
};

const onAuthSuccess = async () => {
  try {
    await customer.loadProfile(business.id);
  } catch (e) {
    report(e);

    const errorMesssage =
      e instanceof Error ? e.message : typeof e === "string" ? e : undefined;

    if (errorMesssage) {
      toastError(errorMesssage);
    } else {
      toastUnexpectedError();
    }

    isLoadingVerificationCode.value = false;
  }

  emits("success");
};
const hasManuallySelectedOtp = ref(false);

const isEmailSelected = ref(false);
const onOtpSent = (channel: string) => {
  view.value = "otp-validation";
  isEmailSelected.value = channel === "email";
  hasManuallySelectedOtp.value = true;
};
</script>

<template>
  <div :class="css('')">
    <OrgSignupForm
      v-if="view === 'form'"
      v-model="newCustomer"
      :country="business.country"
      :title="t('components.sign_up_customer.signup_form_title')"
      :headline="t('components.sign_up_customer.signup_form_subtitle')"
      :cta="t('components.sign_up_customer.signup_form_cta')"
      :name-field-label="t('components.sign_up_customer.name_field_label')"
      :phone-field-label="t('components.sign_up_customer.phone_field_label')"
      :errors="signupFormErrors"
      :is-loading="false"
      :show-close-button="false"
      accepts-terms-conditions-label=""
      :accepts-marketing-communications-label="
        t('components.sign_up_customer.marketing_preferences')
      "
      @submit="onSignupSubmit"
    >
      <template
        v-if="!business.termsAndConditionsLink && !business.privacyPolicyLink"
        #tcs
      >
        <AtomText type="body" color="color-base-40">
          <I18nTranslate
            keypath="components.sign_up_customer.terms_and_conditions.sentence"
          >
            <AtomLink :to="DELIVERECT_TERMS_AND_CONDITION" target="_blank">
              {{ t("components.sign_up_customer.terms_and_conditions.tc") }}
            </AtomLink>
            <AtomLink :to="DELIVERECT_PRIVACY_POLICY" target="_blank">
              {{ t("components.sign_up_customer.terms_and_conditions.pp") }}
            </AtomLink>
          </I18nTranslate>
        </AtomText>
      </template>
      <template
        v-else-if="
          business.termsAndConditionsLink && business.privacyPolicyLink
        "
        #tcs
      >
        <AtomText type="body" color="color-base-40">
          <I18nTranslate
            keypath="components.sign_up_customer.terms_and_conditions.sentence_two"
          >
            <template #businessName>
              {{ business.name }}
            </template>
            <template #businessTc>
              <AtomLink :to="business.termsAndConditionsLink" target="_blank">
                {{
                  t(
                    "components.sign_up_customer.terms_and_conditions.business_tc",
                  )
                }}
              </AtomLink>
            </template>
            <template #businessPP>
              <AtomLink :to="business.privacyPolicyLink" target="_blank">
                {{
                  t(
                    "components.sign_up_customer.terms_and_conditions.business_pp",
                  )
                }}
              </AtomLink>
            </template>
            <template #deliverectTc>
              <AtomLink :to="DELIVERECT_TERMS_AND_CONDITION" target="_blank">
                {{ t("components.sign_up_customer.terms_and_conditions.tc") }}
              </AtomLink>
            </template>
            <template #deliverectPp>
              <AtomLink :to="DELIVERECT_PRIVACY_POLICY" target="_blank">
                {{ t("components.sign_up_customer.terms_and_conditions.pp") }}
              </AtomLink>
            </template>
          </I18nTranslate>
        </AtomText>
      </template>
      <template
        v-else-if="
          business.termsAndConditionsLink || business.privacyPolicyLink
        "
        #tcs
      >
        <AtomText type="body" color="color-base-40">
          <I18nTranslate
            keypath="components.sign_up_customer.terms_and_conditions.sentence_three"
          >
            <template #businessName>
              {{ business.name }}
            </template>
            <template #businessLegal>
              <AtomLink
                :to="
                  business.termsAndConditionsLink
                    ? business.termsAndConditionsLink
                    : business.privacyPolicyLink
                "
                target="_blank"
              >
                {{
                  business.termsAndConditionsLink
                    ? t(
                        "components.sign_up_customer.terms_and_conditions.business_tc",
                      )
                    : t(
                        "components.sign_up_customer.terms_and_conditions.business_pp",
                      )
                }}
              </AtomLink>
            </template>
            <template #deliverectTc>
              <AtomLink :to="DELIVERECT_TERMS_AND_CONDITION" target="_blank">
                {{ t("components.sign_up_customer.terms_and_conditions.tc") }}
              </AtomLink>
            </template>
            <template #deliverectPp>
              <AtomLink :to="DELIVERECT_PRIVACY_POLICY" target="_blank">
                {{ t("components.sign_up_customer.terms_and_conditions.pp") }}
              </AtomLink>
            </template>
          </I18nTranslate>
        </AtomText>
      </template>
    </OrgSignupForm>
    <OtpDeliverySelector
      v-if="view === 'otp-request'"
      :email="props.email"
      :country-code="newCustomer.phoneNumber.countryCode?.toString()"
      :phone-number="newCustomer.phoneNumber.number?.toString() ?? ''"
      :throttle-seconds="props.throttleSeconds"
      @success="onOtpSent"
      @failure="() => emits('failure')"
    />
    <OrgValidateOtp
      v-if="view === 'otp-validation'"
      :title="t('components.sign_up_customer.request_otp_title')"
      :is-loading="isLoadingVerificationCode"
      :show-close-button="false"
      :headline="t('components.sign_up_customer.request_otp_subtitle')"
      :phone-number="
        props.skipOtpSelector && !hasManuallySelectedOtp
          ? props.email
          : isEmailSelected
            ? props.email
            : displayPhoneNumber
      "
      :resend-throttle-sec="props.throttleSeconds"
      :error-message="otpErrorMessage ?? ''"
      :resend-message="t('components.sign_up_customer.resend_otp_label')"
      :otp-length="5"
      :cta="t('components.sign_up_customer.request_otp_cta')"
      @validate="(val) => onValidateOtp(val.pinCode)"
      @resend="() => (view = 'otp-request')"
    />
  </div>
</template>
